import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuX, LuCheck, LuLock, LuShieldCheck, LuSparkles, LuArrowRight, LuUsers, LuClock } from 'react-icons/lu';
import './PaywallModal.css';

const PaywallModal = ({ isOpen, onClose, trialStatus }) => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('annual');

  if (!isOpen) return null;

  const isExpired = trialStatus?.is_expired || trialStatus?.paywall_state === 'expired';
  const installedCount = trialStatus?.installed_apps_count || 1;
  const trackedHours = trialStatus?.total_tracked_hours || 0;
  const canUseFree = installedCount <= 5;

  const handleSelectPlan = (planKey) => {
    onClose?.();
    navigate(`/billing?plan=${planKey}&cycle=${billingCycle}`);
  };

  const handleContinueFree = () => {
    onClose?.();
    navigate('/billing?plan=free');
  };

  const handleManageTeam = () => {
    onClose?.();
    navigate('/settings/manage-teams');
  };

  return (
    <div className="paywall-overlay" role="dialog" aria-modal="true">
      <div className="paywall-modal-container">
        {/* Only allow closing if trial is not hard-expired */}
        {!isExpired && (
          <button type="button" className="paywall-close-btn" onClick={onClose} aria-label="Close modal">
            <LuX />
          </button>
        )}

        <div className="paywall-header">
          <div className="paywall-badge">
            {isExpired ? <LuLock /> : <LuSparkles />}
            <span>{isExpired ? 'Trial Concluded' : 'Upgrade Workspace'}</span>
          </div>
          <h2 className="paywall-title">
            {isExpired ? 'Your 14-Day Free Trial Has Ended' : 'Keep Your Team Tracking Uninterrupted'}
          </h2>
          <p className="paywall-subtitle">
            During your trial, your team logged <strong>{Math.round(trackedHours)} hours</strong> across <strong>{installedCount} connected members</strong>.
            Choose a plan to maintain continuous telemetry, automated payroll, and timelapse recordings.
          </p>

          <div className="paywall-stats-row">
            <div className="paywall-stat-card">
              <LuClock className="stat-icon" />
              <div className="stat-content">
                <span className="stat-val">{trackedHours} hrs</span>
                <span className="stat-lbl">Tracked Work Duration</span>
              </div>
            </div>
            <div className="paywall-stat-card">
              <LuUsers className="stat-icon" />
              <div className="stat-content">
                <span className="stat-val">{installedCount} Members</span>
                <span className="stat-lbl">Active Desktop Clients</span>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="paywall-cycle-toggle">
          <button
            type="button"
            className={`cycle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly Billing
          </button>
          <button
            type="button"
            className={`cycle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
            onClick={() => setBillingCycle('annual')}
          >
            Annual Billing <span className="discount-pill">Save 20%</span>
          </button>
        </div>

        {/* Plan Cards */}
        <div className="paywall-plans-grid">
          {/* Pro Plan */}
          <div className="paywall-plan-card pro-card">
            <div className="plan-card-header">
              <h3 className="plan-name">Pro Plan</h3>
              <p className="plan-target">For growing teams wanting automated monitoring</p>
              <div className="plan-price-block">
                <span className="plan-price">{billingCycle === 'annual' ? '$5.60' : '$7'}</span>
                <span className="plan-period">/ user / mo</span>
              </div>
            </div>

            <ul className="plan-features-list">
              <li><LuCheck className="chk-icon" /> Unlimited teams & members</li>
              <li><LuCheck className="chk-icon" /> 365-day screenshot & activity retention</li>
              <li><LuCheck className="chk-icon" /> 1-minute screenshot intervals</li>
              <li><LuCheck className="chk-icon" /> Automated timelapse video recordings</li>
              <li><LuCheck className="chk-icon" /> Timesheet & attendance export (PDF/CSV)</li>
              <li><LuCheck className="chk-icon" /> Priority email & chat support</li>
            </ul>

            <button
              type="button"
              className="paywall-plan-btn pro-btn"
              onClick={() => handleSelectPlan('pro')}
            >
              Choose Pro Plan <LuArrowRight />
            </button>
          </div>

          {/* Scale Plan */}
          <div className="paywall-plan-card scale-card featured">
            <div className="featured-ribbon">Most Comprehensive</div>
            <div className="plan-card-header">
              <h3 className="plan-name">Scale Plan</h3>
              <p className="plan-target">Complete workforce, payroll & security automation</p>
              <div className="plan-price-block">
                <span className="plan-price">{billingCycle === 'annual' ? '$11.20' : '$14'}</span>
                <span className="plan-period">/ user / mo</span>
              </div>
            </div>

            <ul className="plan-features-list">
              <li><LuCheck className="chk-icon" /> <strong>Everything in Pro Plan</strong></li>
              <li><LuCheck className="chk-icon" /> Real-time security alerts (absence, idle, late)</li>
              <li><LuCheck className="chk-icon" /> Automated payroll calculation & salary slips</li>
              <li><LuCheck className="chk-icon" /> Company holiday & leave policy management</li>
              <li><LuCheck className="chk-icon" /> Keyboard & mouse activity index</li>
              <li><LuCheck className="chk-icon" /> REST API access & lock signout controls</li>
            </ul>

            <button
              type="button"
              className="paywall-plan-btn scale-btn"
              onClick={() => handleSelectPlan('premium')}
            >
              Upgrade to Scale Plan <LuArrowRight />
            </button>
          </div>
        </div>

        {/* Degraded Free Fallback Option */}
        <div className="paywall-footer-fallback">
          {canUseFree ? (
            <p className="fallback-text">
              Need only basic tracking for up to 5 members?{' '}
              <button type="button" className="fallback-link" onClick={handleContinueFree}>
                Continue with Free Plan (5 seats max, 14-day history)
              </button>
            </p>
          ) : (
            <p className="fallback-text">
              Your organization currently has {installedCount} active members. The Free Plan is limited to 5 seats.{' '}
              <button type="button" className="fallback-link" onClick={handleManageTeam}>
                Manage active employees to reduce headcount
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
